import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import { useTranslation } from "react-i18next";
import { Text, TouchableRipple } from "react-native-paper";
import { isMultiSelect } from "../../../helpers/session";
import ItemWrapper from "../../ItemWrapper";
import MCQ from "./variants/MCQ";
import NoteCompletion from "./variants/NoteCompletion";

interface Props {
	item: BaseItemSchema;
	originalItemType: OriginalItemTypes;
	mappedItemType: RenderableItemTypes;
	selectedChoiceIds: string[] | undefined;
	correctAnswerIds: string[] | undefined;
	crossedOutChoiceIds: string[] | undefined;
	isAnswered?: boolean;
	isSummativeTest?: boolean;
	onSelectChoice: (choiceId: string, crossingOut?: boolean) => void;
	currentUserResponse?: unknown;
	toggleShowReadMore?: () => void;
	handleHighlightedTextPress?: () => void;
	correctAnswerOrders?: string[];
	updateFormStates: (formStates: Record<string, string>) => void;
	currentAnswerFormStates?: Record<
		string,
		{ isCorrect?: boolean; response?: string }
	>;
}

const BaseItemType = ({
	item,
	selectedChoiceIds,
	correctAnswerIds,
	crossedOutChoiceIds,
	isAnswered,
	isSummativeTest,
	onSelectChoice,
	toggleShowReadMore,
	originalItemType,
	mappedItemType,
	handleHighlightedTextPress,
	correctAnswerOrders,
	updateFormStates,
	currentAnswerFormStates,
}: Props) => {
	const { isMobile } = useDevice();

	const { content } = item;

	const multiSelect = isMultiSelect({
		maxChoiceCount: item.content?.maxChoiceCount,
		minChoiceCount: item.content?.minChoiceCount,
		mappedItemType,
	});

	const showHint = !(isSummativeTest || isAnswered);
	const instructions = content?.instruction || "Select the best answer";

	const showButtonOpenPassage = item.metadata?.type === "INSERT_TEXT";

	const theme = useAppTheme();
	const hasInserted = !!selectedChoiceIds?.length;

	const { t } = useTranslation();

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const ButtonOpenPassage = () => {
		return (
			<TouchableRipple
				onPress={isMobile ? toggleShowReadMore : undefined}
				pointerEvents={isMobile ? "auto" : "none"}
				style={{
					alignItems: "center",
					margin: isMobile ? 16 : 0,
					padding: 12,
					// borderRadius: theme.roundness,
					borderWidth: 1,
					backgroundColor: hasInserted ? theme.colors.primary : "transparent",
					borderColor: theme.colors.primary,
				}}
			>
				<Box
					flexDirection="row"
					width="100%"
					justifyContent="space-between"
					alignItems="center"
				>
					<Text
						style={{
							color: theme.colors[hasInserted ? "white" : "onSurfaceVariant"],
						}}
						variant="labelLarge"
					>
						{hasInserted
							? t("session.passageStem.insertedAtPosition", {
									position:
										(item.content?.choices?.findIndex((choice) =>
											selectedChoiceIds.includes(choice.id),
										) ?? 0) + 1,
								})
							: t("session.passageStem.notYetInserted")}
					</Text>
					{isMobile && (
						<Text
							variant="labelMedium"
							style={{
								textTransform: "uppercase",
								color: theme.colors[hasInserted ? "white" : "primary"],
							}}
						>
							{t("session.passageStem.openPassage")}
						</Text>
					)}
				</Box>
			</TouchableRipple>
		);
	};
	return (
		<ItemWrapper
			item={item}
			instructions={instructions}
			toggleShowReadMore={toggleShowReadMore}
			originalItemType={originalItemType}
			mappedItemType={mappedItemType}
			handleHighlightedTextPress={handleHighlightedTextPress}
			selectedChoiceIds={selectedChoiceIds}
		>
			{showButtonOpenPassage && <ButtonOpenPassage />}

			{(() => {
				switch (mappedItemType) {
					case "MCQ":
					case "SATA":
					case "SEQUENCE":
						return (
							<MCQ
								multiSelect={multiSelect}
								isSummativeTest={isSummativeTest}
								isAnswered={isAnswered}
								selectedChoiceIds={selectedChoiceIds}
								correctAnswerIds={correctAnswerIds}
								crossedOutChoiceIds={crossedOutChoiceIds}
								choices={content?.choices ?? []}
								currentItem={item}
								handleSelectChoice={onSelectChoice}
								showHint={showHint}
								isMobile={isMobile}
								mappedItemType={mappedItemType}
								correctAnswerOrders={correctAnswerOrders}
							/>
						);

					case "NOTE_COMPLETION":
						return item.response ? (
							<NoteCompletion
								itemResponse={item.response}
								key={item.id}
								countryCode={item.metadata?.countryCode}
								updateFormStates={updateFormStates}
								disabled={isAnswered}
								correct={currentAnswerFormStates}
							/>
						) : null;

					default:
						return null;
				}
			})()}
		</ItemWrapper>
	);
};

export default BaseItemType;
